import cn from 'classnames';
import isNil from 'lodash/isNil';
import { Props, TLocksButtonVisibility } from './types';
import { MainWrap, PathSelectModal } from './styles';
import { memo, useEffect, useMemo, useState } from 'react';
import { TourType } from '../buger-menu/types';

export const GameLockPathModal = memo(
  ({
    pathSettings,
    handleNextStep,
    handlePreviousStep,
    translation = {},
    title,
    description,
    stepIconUrl = null,
    isEnablePreviousStep,
    isEnableNextStep,
    gameId,
    pathLockId,
    isRtl,
    delayedDisable,
    currentPathStepId,
    pathfinderDataState,
    setPathfinderDataState,
    useRecoilValue,
    recoilStateObj,
    asyncStore,
    pathSnapshot,
    theme,
    color,
    APP_AWS,
    pathLocks,
    handleChangePath,
    forceShowPathSwitchMobileModal,
    setForceShowPathSwitchMobileModal,
    buttonOnlyMode,
    nextButtonVisibility,
    finishedPaths,
    icon,
    distance
  }: Props) => {
    const {
      colorBackground,
      opacityBackground,
      colorButton,
      opacityButton,
      colorButtonText,
      opacityButtonText,
      colorText,
      opacityText,
      titleColorText,
      titleOpacityText
    } = pathSettings;

    const { bottomBarOpenState, tourConfigsState } = recoilStateObj || {};
    const isBbOpen = useRecoilValue(bottomBarOpenState);
    const { tourType } = useRecoilValue(tourConfigsState);
    const [showPathSwitchMobileModal, setShowPathSwitchMobileModal] = useState(false);
    const { device } = theme || {};
    const { mobile, landscape } = device || {};
    const hideConfigsBlock = document.querySelector('.ec-bb-main .hideConfigs');
    let bbOpenBottom = tourType === TourType.ECommerce ? '262px' : '235px';
    let bbClosedBottom =
      (hideConfigsBlock && !landscape) ||
      (tourType !== TourType.ECommerce && tourType !== TourType.Game)
        ? '76px'
        : '55px';
    if (mobile && landscape && tourType === TourType.ECommerce) {
      bbOpenBottom = '187px';
    }
    if (
      mobile &&
      landscape &&
      tourType !== TourType.ECommerce &&
      tourType !== TourType.Game &&
      !isBbOpen
    ) {
      bbOpenBottom = '52px';
    }
    if (tourType === TourType.Game) {
      bbOpenBottom = '155px';
      if (mobile) {
        bbClosedBottom = '34px';
      } else {
        bbClosedBottom = '55px';
      }
    }

    useEffect(() => {
      //saving Pathfinder data to recoil to use replica of it on VR
      if (
        currentPathStepId !== pathfinderDataState?.currentPathStepId ||
        isEnablePreviousStep !== pathfinderDataState?.isEnablePreviousStep
      ) {
        setPathfinderDataState({
          pathSettings,
          handleNextStep,
          handlePreviousStep,
          translation,
          description,
          isEnablePreviousStep,
          gameId,
          pathLockId,
          isRtl,
          delayedDisable,
          currentPathStepId,
          title,
          titleColorText,
          titleOpacityText
        });
      }
    }, [
      pathSettings,
      handleNextStep,
      handlePreviousStep,
      translation,
      description,
      isEnablePreviousStep,
      gameId,
      pathLockId,
      isRtl,
      delayedDisable
    ]);

    const pathIconUrl = pathSettings?.icon?.includes('http')
      ? pathSettings?.icon
      : `${APP_AWS}/games/${gameId}/locks/${pathLockId}/${pathSettings?.icon}`;

    const currentStep = (asyncStore?.pathIds || [])?.indexOf(currentPathStepId) + 1;
    const stepsLength = asyncStore?.pathIds?.length;
    const handleCloseSwitchModal = () => {
      setForceShowPathSwitchMobileModal(false);
      setShowPathSwitchMobileModal(false);
    };

    const showNextButton = useMemo(
      () =>
        !(
          nextButtonVisibility === TLocksButtonVisibility.HideAll ||
          (nextButtonVisibility === TLocksButtonVisibility.HideLast && currentStep === stepsLength)
        ),
      [nextButtonVisibility, currentStep, stepsLength]
    );

    const onChangePath = (id: number) => () => {
      setForceShowPathSwitchMobileModal(false);
      setShowPathSwitchMobileModal(false);
      if (handleChangePath) handleChangePath({ target: { value: id } });
    };
    const togglePathSwitch = () => {
      setForceShowPathSwitchMobileModal((e) => !e);
      setShowPathSwitchMobileModal((e) => !e);
    };

    const NavBlock = () => (
      <div className="glpm-navWrap">
        {pathSettings?.stepEnabled && (
          <div className="glpm-stepsDesk glpm-stepsDesk-btns rtl-mr-5" onClick={togglePathSwitch}>
            <i className="fas fa-route rtl-ml-5" style={{ marginRight: '5px' }} />
            {translation.step || 'Step'}: {currentStep}/{stepsLength}
          </div>
        )}
        {isEnablePreviousStep && (
          <a
            className="prev-btn pf-btn"
            onClick={handlePreviousStep}
            style={{
              background: colorButtonText || 'none',
              opacity: opacityButtonText,
              border: `1px solid ${colorButton}`
            }}
          >
            <span className="btn-text">
              <i
                className="fas fa-arrow-left rtl-icon-rotate"
                style={{
                  color: colorButton,
                  opacity: opacityButton
                }}
              />
            </span>
          </a>
        )}
        {showNextButton && (
          <button
            type="button"
            className="next-btn pf-btn rtl-mr-5"
            onClick={handleNextStep}
            disabled={!isEnableNextStep}
            style={{
              background: isEnableNextStep ? colorButton || 'none' : 'gray',
              opacity: opacityButton
            }}
          >
            <span
              className="btn-text"
              style={{
                color: colorButtonText,
                opacity: opacityButtonText
              }}
            >
              {translation.next || 'Next'}
            </span>
            <i
              className="fa-solid fa-arrow-right rtl-icon-rotate rtl-mr-5"
              style={{
                color: colorButtonText,
                opacity: opacityButtonText
              }}
            />
          </button>
        )}
      </div>
    );
    return (
      <>
        {(forceShowPathSwitchMobileModal || showPathSwitchMobileModal) && mobile && (
          <PathSelectModal>
            <div className="psm-header">
              <div>{translation.multipathSwitcher || 'Multipath switcher'}</div>
              <div className="psm-closeBlock" onClick={handleCloseSwitchModal}>
                <i className="far fa-times" />
              </div>
            </div>
            <div className="psm-items-wrap">
              {pathLocks?.map((p) => {
                const isFinished = finishedPaths?.includes(p.id);
                //use this if p.id = currentLock
                let currentStep;
                let stepsLength;
                if (p.id === pathLockId) {
                  currentStep = (asyncStore?.pathIds || [])?.indexOf(currentPathStepId) + 1;
                  stepsLength = asyncStore?.pathIds?.length;
                } else if (pathSnapshot[p.id]) {
                  currentStep = (pathSnapshot[p.id]?.pathIds || [])?.indexOf(currentPathStepId) + 1;
                  stepsLength = pathSnapshot[p.id]?.pathIds?.length;
                } else {
                  currentStep = 1;
                  stepsLength = p?.paths?.length;
                }
                //if not, we need to check is it exists on pathSnapshot
                //if yes - get data from there
                //if no - cound steps and set 0 / length
                return (
                  <div className="psm-item" onClick={isFinished ? undefined : onChangePath(p.id)}>
                    <div className="psm-imgDeskWrap">
                      <div
                        className="psm-imgWrap"
                        style={{
                          backgroundImage: `url(${`${APP_AWS}/games/${gameId}/locks/${p.id}/${p.pathSetting?.icon}`})`
                        }}
                      />
                      <div className="psm-deskWrap">
                        <div className="psm-name">{p.name}</div>
                        {isFinished ? (
                          <div className="psm-steps">{translation.finished || 'finished'}</div>
                        ) : (
                          <div className="psm-steps">
                            {currentStep}/{stepsLength}{' '}
                            {translation.stepsCompleted || 'steps completed'}
                          </div>
                        )}
                      </div>
                    </div>
                    <div className="psm-next">
                      <i className="far fa-arrow-right" />
                    </div>
                  </div>
                );
              })}
            </div>
          </PathSelectModal>
        )}
        <MainWrap
          className={cn(
            'path-lock-panel-wrap',
            mobile && 'mobile',
            landscape && 'landscape',
            isBbOpen ? 'bbOpen' : 'bbClosed',
            tourType !== TourType.ECommerce && tourType !== TourType.Game && 'regular',
            tourType === TourType.Game && 'game',
            tourType === TourType.ECommerce && 'eCommerce',
            hideConfigsBlock && 'hideConfigsBlock'
          )}
          style={{
            //for regular tour mobile landscape bottom will be 52
            bottom: isBbOpen ? bbOpenBottom : bbClosedBottom
          }}
        >
          {buttonOnlyMode ? (
            <div
              className={cn(
                'path-lock-panel',
                mobile && 'mobile',
                delayedDisable && 'delayedDisable',
                (description?.length || 0) < 1 && 'noDesk'
              )}
              style={{ backdropFilter: 'none', justifyContent: 'center' }}
            >
              <NavBlock />
            </div>
          ) : (
            <>
              {pathLockId && (
                <div
                  className={cn(
                    'path-lock-panel',
                    mobile && 'mobile',
                    delayedDisable && 'delayedDisable',
                    (description?.length || 0) < 1 && 'noDesk'
                  )}
                  style={{
                    backgroundColor: color.hexToRgbA(colorBackground, opacityBackground),
                    direction: isRtl ? 'rtl' : 'initial'
                  }}
                >
                  {!mobile && (pathSettings?.icon || icon) && (
                    <div
                      className={cn(
                        'path-lock-iconWrap',
                        (description?.length || 0) > 120 && 'bigger'
                      )}
                    >
                      <img
                        className={cn(mobile && 'mobile')}
                        src={icon || stepIconUrl || pathIconUrl}
                        alt="path_image"
                      />
                    </div>
                  )}
                  <div className="glpm-deskWrap" style={{ flex: 1 }}>
                    <div
                      className="path-lock-titleWrap"
                      style={{ color: titleColorText, opacity: titleOpacityText }}
                    >
                      {title && pathSettings?.titleEnabled && (
                        <div className="path-lock-title">{title}</div>
                      )}
                      {pathSettings?.stepEnabled && (
                        <div className="glpm-stepsDesk rtl-mr-5" onClick={togglePathSwitch}>
                          <i className="fas fa-route rtl-ml-5" style={{ marginRight: '5px' }} />
                          {translation.step || 'Step'}: {currentStep}/{stepsLength}
                          {!isNil(distance) && ` ${distance} m.`}
                        </div>
                      )}
                    </div>
                    <div
                      className="path-lock-description"
                      dangerouslySetInnerHTML={{ __html: description || '' }}
                      style={{ color: colorText, opacity: opacityText }}
                    />
                  </div>
                  <NavBlock />
                </div>
              )}

              <div className="pl-subtitles-space" />
            </>
          )}
        </MainWrap>
      </>
    );
  }
);
